import { createContext, useContext, useMemo, useState, useEffect, FC } from 'react'
import { useInitializeCookies } from 'hooks/useInitializeCookies'
import { storePreservedParams } from 'utils/preserve-query'

type CookieContextType = {
  cookieLocale: string
  setCookieLocale: (locale: string) => void
  cookieCountry: string
  setCookieCountry: (country: string) => void
  consentNecessary: boolean
  consentAnalytical: boolean
  consentMarketing: boolean
  cookies: { [key: string]: string }
  setCustomCookie: (name: string, value: string, options?: object) => void
}

export const CookieContext = createContext<CookieContextType | null>(null)

export const CookieProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  const contextValue = useInitializeCookies()

  // Store preserved params (runs once client-side)
  useEffect(() => {
    if (isClient) {
      storePreservedParams(contextValue.setCustomCookie)
    }
  }, [isClient])

  const memoizedValue = useMemo(() => contextValue, [JSON.stringify(contextValue)])

  if (!isClient) return <>{children}</>

  return <CookieContext.Provider value={memoizedValue}>{children}</CookieContext.Provider>
}

export const useCookieContext = () => {
  const context = useContext(CookieContext)
  if (!context) {
    throw new Error('useCookieContext must be used within a CookieProvider')
  }
  return context
}
